import * as React from 'react';
import { Ic } from '../shell/Icons';
import { relativeTime, severityOf, STATE_PILL, TYPE_LABEL } from './notifUtils';
import { navClickHandler } from '../../utils/navClick';

/**
 * One notification row — shared by the bell popover and the Settings list.
 *
 * A per-row IntersectionObserver fires `onVisible(id)` the first time the row
 * genuinely scrolls into view (updated design: `seen` = scrolled into view, NOT
 * "popover opened"). It only arms for `unseen` rows and disarms after firing.
 *
 * `showState` renders the state pill as the primary column (Settings page); the
 * compact popover omits it.
 */
export default function NotificationRow({ n, onVisible, onClick, showState = false, active = false }) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!onVisible || n.state !== 'unseen') return undefined;
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      onVisible(n.id); // environments without IO: treat as visible
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          onVisible(n.id);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [n.id, n.state, onVisible]);

  const sev = severityOf(n.severity);
  const pill = STATE_PILL[n.state] || STATE_PILL.viewed;
  const unread = n.state === 'unseen';

  // Issue #339: a real <a href> (both the bell popover and the Settings list
  // resolve to the SAME canonical deep-link, `/account/notifications?focus=<id>`
  // — see NotificationBell's `goDetail` / this page's own `?focus=` read) so a
  // middle-click / ctrl-click opens the notification in a new tab; a plain left
  // click still runs the caller's `onClick` (open inline, mark viewed, etc.)
  // instead of a real page navigation.
  const href = `/account/notifications?focus=${encodeURIComponent(n.id)}`;

  return (
    <a
      ref={ref}
      href={href}
      className={`notif-row${active ? ' active' : ''}`}
      data-testid={`notif-row-${n.id}`}
      data-state={n.state}
      onClick={navClickHandler(() => onClick?.(n))}
    >
      <span
        className={`dot ${sev.dot}`}
        title={sev.label}
        aria-label={sev.label}
        style={{ marginTop: 6 }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="notif-row-top">
          <span className="badge neutral notif-type">{TYPE_LABEL[n.type] || n.type}</span>
          {showState && (
            <span className={`badge ${pill.badge}`} data-testid={`notif-state-${n.id}`}>{pill.label}</span>
          )}
          <span className="grow" />
          <span className="notif-time"><Ic name="clock" sm /> {relativeTime(n.created_at)}</span>
        </div>
        <div className={`notif-title${unread ? ' unread' : ''}`}>{n.title}</div>
        {n.body ? <div className="notif-body">{n.body}</div> : null}
      </div>
    </a>
  );
}
